import { FC, useCallback, useEffect } from 'react';
import {
  Controller,
  ControllerRenderProps,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';
import {
  PeriodUnit,
  ProductFormData,
  ReBillTimesType,
  AddonPerformanceAccountFee,
} from '@src/types';
import { Checkbox, Input, InputSelect, Switch } from '@features/ui';
import {
  currencyData,
  dateSelectData,
  rebillTimesTypeData,
} from '@features/admin/productsAddons/productsAddonsData.ts';
import { cn } from '@lib/utils.ts';
import {
  productBillingDefault,
  productTypeDefault,
} from '@features/admin/productsAddons/productDefault.ts';

export const ProductBillingForm: FC<UseFormReturn<ProductFormData>> = ({
  control,
  formState: { errors },
  reset,
  getValues,
  setValue,
}) => {
  const currency = useWatch({ control, name: 'currency' });
  const rebillTimesType = useWatch({ control, name: 'rebill_times_type' });
  const firstPeriodUnit = useWatch({ control, name: 'first_period_unit' });
  const addonPerformanceAccountFeeOption = useWatch({
    control,
    name: 'addon_performance_account_fee_option' as any,
  });

  const addonType = useWatch({ control, name: 'addon_type' as any });

  const currencyValue = currencyData.find(
    (item) => item.value === currency
  )?.label;

  const isLifetimePayPartial =
    addonPerformanceAccountFeeOption ===
    AddonPerformanceAccountFee.LIFETIME_PAY_PARTIAL;

  const disableLifeTimes =
    firstPeriodUnit === PeriodUnit.LIFETIMES && !isLifetimePayPartial;

  const disableSecond =
    (rebillTimesType === ReBillTimesType.NoMoreCharges || !rebillTimesType) &&
    !isLifetimePayPartial;

  // Auto-set first_period_unit to LIFETIMES when addon_performance_account_fee_option is LIFETIME_PAY_PARTIAL
  useEffect(() => {
    if (isLifetimePayPartial && firstPeriodUnit !== PeriodUnit.LIFETIMES) {
      setValue('first_period_unit', PeriodUnit.LIFETIMES);
    }
  }, [isLifetimePayPartial, firstPeriodUnit, setValue]);

  const handleChangeRebillTimesType = useCallback(
    (
      field: ControllerRenderProps<ProductFormData, 'rebill_times_type'>,
      value: string
    ) => {
      field.onChange(value);
      const currentValues = getValues();

      reset({ ...currentValues, rebill_times_unit: undefined });
    },
    [reset, getValues]
  );

  const handleChangeFirstPeriodUnit = useCallback(
    (
      field: ControllerRenderProps<ProductFormData, 'first_period_unit'>,
      value: string
    ) => {
      field.onChange(value);
      const currentValues = getValues();

      if (value === PeriodUnit.LIFETIMES) {
        reset({
          ...currentValues,
          ...productTypeDefault,
          ...productBillingDefault,
        });
      }
    },
    [reset, getValues]
  );

  return (
    <div className={'mt-[76px] text-xs'}>
      <p className={'text-sm font-bold'}>Billing</p>
      <Controller
        control={control}
        name="name"
        render={({ field }) => (
          <div
            className={
              'mt-4 mb-11.5 border-b border-[#CECECE] flex items-center'
            }
          >
            <div
              className={cn(
                'text-white text-sm px-3 h-8 inline-flex items-center gap-2 bg-[#171717]'
              )}
            >
              <p>{field.value}</p>
            </div>
          </div>
        )}
      />
      <div
        className={
          'flex lg:flex-row flex-col xl:w-[80%] w-full gap-x-[81px] gap-y-6'
        }
      >
        <Controller
          control={control}
          name={`currency`}
          render={({ field }) => (
            <div className={'flex flex-1 flex-col gap-1'}>
              <p className={'ml-2 mb-1'}>Currency</p>
              <InputSelect
                className={'text-xs'}
                options={currencyData}
                value={field.value}
                onChange={(value) => field.onChange(value)}
              />
              <p>
                You can choose from the list of currencies supported by
                pay-systems
              </p>
            </div>
          )}
        />
        <Controller
          control={control}
          name={`first_price`}
          render={({ field }) => {
            return (
              <div className={'flex flex-1 flex-col gap-1'}>
                <p className={'ml-2 mb-1'}>First Price</p>
                <Input
                  error={!!errors.first_price?.message}
                  placeholder={'0'}
                  textSize={'xs'}
                  type={'number'}
                  variant={'secondary'}
                  {...field}
                  endIcon={<div className={'text-white'}>{currencyValue}</div>}
                />
                <p>
                  You can choose from the list of currencies supported by
                  pay-systems
                </p>
              </div>
            );
          }}
        />
        <div className={'flex flex-1 flex-col gap-2'}>
          <p className={'ml-2'}>First Period</p>
          <div className={'flex gap-x-1.5'}>
            <Controller
              control={control}
              name={`first_period_value`}
              render={({ field }) => (
                <div className={'w-12'}>
                  <Input
                    {...field}
                    className={'w-12'}
                    disabled={disableLifeTimes}
                    error={!!errors.first_period_value?.message}
                    placeholder={'0'}
                    textSize={'xs'}
                    type={'number'}
                    value={field.value ?? ''}
                    variant={'secondary'}
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name={`first_period_unit`}
              render={({ field }) => (
                <InputSelect
                  className={'text-xs'}
                  options={dateSelectData}
                  value={field.value}
                  onChange={(value) =>
                    handleChangeFirstPeriodUnit(field, value)
                  }
                />
              )}
            />
          </div>
        </div>
      </div>
      <div
        className={
          'flex lg:flex-row flex-col xl:w-[80%] w-full gap-x-[81px] gap-y-6 mt-7.5'
        }
      >
        <Controller
          control={control}
          name={`second_price`}
          render={({ field }) => (
            <div className={'flex flex-1 flex-col gap-1'}>
              <p className={cn('ml-2 mb-1', disableSecond ? 'opacity-50' : '')}>
                Second Price
              </p>
              <Input
                {...field}
                disabled={disableSecond}
                endIcon={<div className={'text-white'}>{currencyValue}</div>}
                placeholder={'0'}
                textSize={'xs'}
                type={'number'}
                value={field.value ?? ''}
                variant={'secondary'}
              />
              <p className={disableSecond ? 'opacity-50' : ''}>
                You can choose from the list of currencies supported by
                pay-systems
              </p>
            </div>
          )}
        />
        <div className={'flex flex-1 flex-col gap-2'}>
          <p className={cn('ml-2', disableSecond ? 'opacity-50' : '')}>
            Second Period
          </p>
          <div className={'flex gap-x-1.5'}>
            <Controller
              control={control}
              name={`second_period_value`}
              render={({ field }) => (
                <div className={'w-12'}>
                  <Input
                    {...field}
                    className={'w-12'}
                    disabled={disableSecond}
                    placeholder={'0'}
                    textSize={'xs'}
                    type={'number'}
                    value={field.value ?? ''}
                    variant={'secondary'}
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name={`second_period_unit`}
              render={({ field }) => (
                <InputSelect
                  className={'text-xs'}
                  disabled={disableSecond}
                  options={dateSelectData}
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                />
              )}
            />
          </div>
        </div>
        <Controller
          control={control}
          name={`addon_monthly_price`}
          render={({ field }) => (
            <div className={'flex flex-1 flex-col gap-1'}>
              <p className={cn('ml-2 mb-1', !addonType ? 'opacity-50' : '')}>
                Addon monthly price
              </p>
              <Input
                {...field}
                disabled={!addonType}
                endIcon={<div className={'text-white'}>{currencyValue}</div>}
                placeholder={'0'}
                textSize={'xs'}
                type={'number'}
                value={field.value ?? ''}
                variant={'secondary'}
              />
            </div>
          )}
        />
      </div>
      <div
        className={
          'flex lg:flex-row flex-col w-full gap-x-[81px] gap-y-6 lg:mt-[23px] mt-6'
        }
      >
        <div
          className={
            'flex lg:flex-row flex-col xl:w-[80%] w-full gap-x-[81px] gap-y-6'
          }
        >
          <div className={'flex flex-1 gap-1.5'}>
            <Controller
              control={control}
              name={`rebill_times_type`}
              render={({ field }) => (
                <div className={'flex flex-col gap-1 w-full'}>
                  <div className={'lg:flex hidden ml-2 mb-1 h-4'} />
                  <InputSelect
                    className={'text-xs'}
                    disabled={disableLifeTimes}
                    options={rebillTimesTypeData}
                    placeholder={'Select'}
                    value={field.value ?? ''}
                    onChange={(value) =>
                      handleChangeRebillTimesType(field, value)
                    }
                  />
                  <p className={disableLifeTimes ? 'opacity-50' : ''}>
                    {`This is number of payments which will occur at the second price`}
                  </p>
                </div>
              )}
            />
            {rebillTimesType === ReBillTimesType.ChargeSecondPriceTimes && (
              <Controller
                control={control}
                name={`rebill_times_unit`}
                render={({ field }) => (
                  <div className={'flex flex-col gap-1'}>
                    <div className={'lg:flex hidden ml-2 mb-1 h-4'} />
                    <Input
                      {...field}
                      className={'w-[48px]'}
                      disabled={disableLifeTimes}
                      placeholder={'0'}
                      textSize={'xs'}
                      type={'number'}
                      variant={'secondary'}
                    />
                  </div>
                )}
              />
            )}
          </div>
          <Controller
            control={control}
            name={`quantity`}
            render={({ field }) => (
              <div className={'flex flex-1 flex-col gap-1'}>
                <p
                  className={cn(
                    'ml-2 mb-1',
                    disableLifeTimes ? 'opacity-50' : ''
                  )}
                >
                  Quantity
                </p>
                <Input
                  {...field}
                  disabled={disableLifeTimes}
                  placeholder={'0'}
                  textSize={'xs'}
                  value={field.value ?? ''}
                  variant={'secondary'}
                />
                <p className={disableLifeTimes ? 'opacity-50' : ''}>
                  Automatically calculated if empty
                </p>
              </div>
            )}
          />
          <Controller
            control={control}
            name={`change_quantity`}
            render={({ field }) => (
              <div className={'flex flex-1 flex-col gap-1'}>
                <p
                  className={cn(
                    'ml-2 mb-1',
                    disableLifeTimes ? 'opacity-50' : ''
                  )}
                >
                  Change Quantity
                </p>
                <Input
                  {...field}
                  disabled={disableLifeTimes}
                  error={!!errors.change_quantity?.message}
                  placeholder={'0'}
                  textSize={'xs'}
                  value={field.value ?? ''}
                  variant={'secondary'}
                />
                <p className={disableLifeTimes ? 'opacity-50' : ''}>
                  Automatically calculated if empty
                </p>
              </div>
            )}
          />
        </div>
        <Controller
          control={control}
          name={`is_enabled`}
          render={({ field }) => (
            <div className={'flex flex-1 flex-col justify-center'}>
              <Switch
                checked={!field.value}
                disabled={disableLifeTimes}
                label={'Enable/ Disable'}
                onCheckedChange={(value) => field.onChange(!value)}
              />
            </div>
          )}
        />
      </div>
      <div
        className={
          'flex lg:flex-row flex-col w-full gap-x-2 gap-y-6 mt-[41px] mb-[39px] items-end'
        }
      >
        <div
          className={cn(
            'flex lg:flex-row flex-col xl:w-[80%] w-full gap-x-[81px] gap-y-6'
          )}
        >
          <Controller
            control={control}
            name={`expire_in_last_day_of_current_month`}
            render={({ field }) => (
              <div className={'flex flex-1'}>
                <Checkbox
                  checked={!!field.value}
                  disabled={disableLifeTimes}
                  label={'Expires in last day of current month'}
                  onCheckedChange={(value) => field.onChange(value)}
                />
              </div>
            )}
          />
          <Controller
            control={control}
            name={`is_apply_tax`}
            render={({ field }) => (
              <div className={'flex-1'}>
                <Checkbox
                  checked={!!field.value}
                  disabled={disableLifeTimes}
                  label={'Apply Tax ?'}
                  onCheckedChange={(value) => field.onChange(value)}
                />
              </div>
            )}
          />
          <Controller
            control={control}
            name={`is_apply_convenience_fee`}
            render={({ field }) => (
              <div className={'flex-1'}>
                <Checkbox
                  checked={!!field.value}
                  disabled={disableLifeTimes}
                  label={'Apply convenience fee ?'}
                  onCheckedChange={(value) => field.onChange(Boolean(value))}
                />
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
};
